import {
  Avatar,
  Badge,
  Box,
  Checkbox,
  HStack,
  Icon,
  IconButton,
  Table,
  Image,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Stack,
  Tooltip,
  Tr,
} from "@chakra-ui/react";
import { InfoOutlineIcon } from "@chakra-ui/icons";

import * as React from "react";
import { FiEdit2, FiInfo } from "react-icons/fi";
import { IoArrowDown } from "react-icons/io5";
import { KYDAdminEvent } from "@common/interfaces/KYDEvent";
import {
  AdminTicketType,
  KYDTicketType,
  TicketTypeStatus,
} from "@common/interfaces/KYDTicket";
import { TieredTicketTypes } from "../utils";

export const TicketTypesTable = ({
  kydEvent,
  ticketTypes,
  tiers,
  onEdit,
  loading,
  ...props
}: {
  kydEvent: KYDAdminEvent;
  ticketTypes: AdminTicketType[];
  tiers: TieredTicketTypes;
  onEdit: (ticketType: AdminTicketType) => void;
  loading: any;
}) => {
  const badgeForStatus = (ticketType: KYDTicketType) => {
    let bg = "gray";
    let name = "N/A";

    if (ticketType.sold_out) {
      bg = "orange";
      name = "Sold Out";
    } else {
      switch (ticketType.status) {
        case "DRAFT":
          bg = "blue";
          name = "Draft";

          break;
        case "PROCESSING":
          bg = "yellow";
          name = "Processing";

          break;
        case "OPEN":
          bg = "green";
          name = "On Sale";

          break;
        case "OFFSALE":
          bg = "red";
          name = "Off Sale";
          break;

        case "DISABLED":
          if (ticketType.tier && ticketType.tier > 0) {
            bg = "gray";
            name = "Not Active";
          }

          break;
        default: {
        }
      }
    }

    return (
      <Box key={ticketType.id}>
        <Badge
          justifyItems={"center"}
          maxW={"100px"}
          size={"xl"}
          colorScheme={bg}
        >
          {name}
        </Badge>
        {ticketType.status === "OPEN" && ticketType.display_off_sale_at ? (
          <Text fontSize={"xs"} color={"gray.500"}>
            Off sale {ticketType.display_off_sale_at}
          </Text>
        ) : null}
        {ticketType.status === "OFFSALE" && ticketType.display_on_sale_at ? (
          <Text fontSize={"xs"} color={"gray.500"}>
            On sale {ticketType.display_on_sale_at}
          </Text>
        ) : null}
      </Box>
    );
  };

  return (
    <Table {...props}>
      <Thead>
        <Tr>
          <Th>Name</Th>
          <Th>Price</Th>
          <Th>Limit</Th>
          <Th>Sold</Th>
          <Th>Revenue</Th>
          <Th>Status</Th>
          <Th></Th>
        </Tr>
      </Thead>
      <Tbody>
        {ticketTypes.map((ticketType: AdminTicketType) => (
          <Tr key={ticketType.id}>
            <Td>
              <Stack>
                <Text fontWeight="medium">{ticketType.name}</Text>
                {tiers[ticketType.id]
                  ? tiers[ticketType.id].map((tier) => (
                      <Text key={tier.id} fontWeight="medium">
                        {tier.name}
                      </Text>
                    ))
                  : null}
                {ticketType.description ? (
                  <Text fontSize={"xs"}>{ticketType.description}</Text>
                ) : null}
                {ticketType.code ? (
                  <Badge
                    textAlign={"center"}
                    maxW={"100px"}
                    isTruncated
                    colorScheme="purple"
                  >
                    {ticketType.code}
                  </Badge>
                ) : null}
              </Stack>
            </Td>
            <Td>
              <Stack>
                <Text fontWeight="medium">
                  {ticketType.free ? "FREE" : ticketType.display_price}
                </Text>
                {tiers[ticketType.id]
                  ? tiers[ticketType.id].map((tier) => (
                      <Text key={tier.id} fontWeight="medium">
                        {tier.free ? "FREE" : tier.display_price}
                      </Text>
                    ))
                  : null}
              </Stack>
            </Td>
            <Td>
              <Text fontWeight="medium">{ticketType.limit}</Text>
            </Td>
            <Td>
              <Stack>
                <HStack>
                  <Text color="muted">
                    {ticketType.total_tickets_purchased_display}
                  </Text>
                  {ticketType.sold_out &&
                  ticketType.total_tickets_purchased !== ticketType.quantity ? (
                    <Tooltip
                      hasArrow
                      label="This ticket type is currently sold out, but it can take up to 30 minutes for this metric to update."
                      fontSize="md"
                    >
                      <InfoOutlineIcon />
                    </Tooltip>
                  ) : null}
                </HStack>
                {tiers[ticketType.id]
                  ? tiers[ticketType.id].map((tier) => (
                      <HStack key={tier.id}>
                        <Text color="muted">
                          {tier.total_tickets_purchased_display}
                        </Text>
                        {tier.sold_out &&
                        tier.total_tickets_purchased !== tier.quantity ? (
                          <Tooltip
                            hasArrow
                            label="This ticket type is currently sold out, but it can take up to 30 minutes for this metric to update."
                            fontSize="md"
                          >
                            <InfoOutlineIcon />
                          </Tooltip>
                        ) : null}
                      </HStack>
                    ))
                  : null}
              </Stack>
            </Td>
            <Td>
              <Stack>
                <Text color="muted">{ticketType.total_gmv_display}</Text>
                {tiers[ticketType.id]
                  ? tiers[ticketType.id].map((tier) => (
                      <Text key={tier.id} color="muted">
                        {tier.total_gmv_display}
                      </Text>
                    ))
                  : null}
              </Stack>
            </Td>
            <Td>
              <Stack>
                {badgeForStatus(ticketType)}
                {tiers[ticketType.id]
                  ? tiers[ticketType.id].map((tier) => badgeForStatus(tier))
                  : null}
              </Stack>
            </Td>
            <Td>
              <HStack spacing="1">
                <IconButton
                  isDisabled={loading["blocking"]}
                  onClick={() => onEdit(ticketType)}
                  icon={<FiEdit2 fontSize="1.25rem" />}
                  variant="ghost"
                  aria-label="Edit member"
                />
              </HStack>
            </Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
};
